import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileXls, FilePdf } from "@phosphor-icons/react";
import { formatIdDate } from "@/lib/format";

/**
 * Presentational sub-components for SPBReport.jsx.
 * All state stays in the parent — these are dumb views.
 */

export function ReportFiltersBar({
  statusTabs, status, onStatusChange,
  periodOptions, period, onPeriodChange,
  dateFrom, onDateFromChange,
  dateTo, onDateToChange,
  onExport,
  activeDateFrom, activeDateTo,
}) {
  return (
    <>
      {/* Status tabs */}
      <div className="flex gap-2" data-testid="status-tabs">
        {statusTabs.map((t) => {
          const Icon = t.icon;
          const active = status === t.value;
          return (
            <button
              key={t.value}
              onClick={() => onStatusChange(t.value)}
              data-testid={`tab-${t.value}`}
              className={`flex items-center gap-2 px-4 py-2 border font-bold uppercase tracking-wider text-sm ${
                active
                  ? t.activeCls
                  : "bg-background border-border text-muted-foreground hover:bg-secondary/40"
              }`}
            >
              <Icon size={16} weight={active ? "fill" : "regular"} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Period + Date filter */}
      <div className="border border-border bg-secondary/20 p-4 space-y-3" data-testid="filters-card">
        <div className="flex flex-wrap gap-2" data-testid="period-tabs">
          {periodOptions.map((p) => (
            <button
              key={p.value}
              onClick={() => onPeriodChange(p.value)}
              data-testid={`period-${p.value}`}
              className={`px-3 py-1.5 border text-sm font-semibold ${
                period === p.value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background border-border text-muted-foreground hover:bg-secondary/40"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="overline block mb-1">Tanggal Dari</Label>
            <Input type="date" value={dateFrom} onChange={(e) => onDateFromChange(e.target.value)} className="rounded-none h-10" data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="overline block mb-1">Tanggal Sampai</Label>
            <Input type="date" value={dateTo} onChange={(e) => onDateToChange(e.target.value)} className="rounded-none h-10" data-testid="filter-date-to" />
          </div>
          <div className="flex items-end gap-2 md:col-span-2 justify-end">
            <Button variant="outline" onClick={() => onExport("xlsx")} className="rounded-none" data-testid="export-xlsx-btn">
              <FileXls size={16} className="mr-2" /> Excel
            </Button>
            <Button variant="outline" onClick={() => onExport("pdf")} className="rounded-none" data-testid="export-pdf-btn">
              <FilePdf size={16} className="mr-2" /> PDF
            </Button>
          </div>
        </div>
        {activeDateFrom && (
          <p className="text-xs text-muted-foreground">
            Periode aktif: <strong>{activeDateFrom}</strong> s/d <strong>{activeDateTo}</strong>
          </p>
        )}
      </div>
    </>
  );
}


export function ReportBucketTable({ buckets, loading }) {
  return (
    <div className="border border-border overflow-x-auto" data-testid="bucket-table">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 border-b border-border">
          <tr className="text-left">
            <th className="px-3 py-2 overline">Periode</th>
            <th className="px-3 py-2 overline text-right">Jumlah SPB</th>
            <th className="px-3 py-2 overline text-right">Total Kuantitas</th>
            <th className="px-3 py-2 overline text-right">Total Termuat</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">Memuat…</td></tr>}
          {!loading && buckets.length === 0 && (
            <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">Tidak ada data untuk filter ini.</td></tr>
          )}
          {buckets.map((b, i) => (
            <tr key={b.bucket} className="border-b border-border/40 hover:bg-secondary/30" data-testid={`bucket-row-${i}`}>
              <td className="px-3 py-2 font-mono font-bold">{b.bucket}</td>
              <td className="px-3 py-2 font-mono text-right">{b.count}</td>
              <td className="px-3 py-2 font-mono text-right">{b.total_jumlah}</td>
              <td className="px-3 py-2 font-mono text-right">{b.total_termuat}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


export function ReportDetailTable({ detail, statusLabel }) {
  return (
    <div className="border border-border overflow-x-auto" data-testid="detail-table">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 border-b border-border">
          <tr className="text-left">
            <th className="px-3 py-2 overline">No SPB</th>
            <th className="px-3 py-2 overline">Tanggal</th>
            <th className="px-3 py-2 overline">No Polisi</th>
            <th className="px-3 py-2 overline">Sopir</th>
            <th className="px-3 py-2 overline">Produk</th>
            <th className="px-3 py-2 overline text-right">Jumlah</th>
            <th className="px-3 py-2 overline text-right">Termuat</th>
            <th className="px-3 py-2 overline">Status</th>
            <th className="px-3 py-2 overline">Distrik</th>
          </tr>
        </thead>
        <tbody>
          {detail.map((r) => (
            <tr key={r.id} className="border-b border-border/40 hover:bg-secondary/30">
              <td className="px-3 py-2 font-mono">{r.no_spb}</td>
              <td className="px-3 py-2 whitespace-nowrap">{formatIdDate(r.tanggal) || "-"}</td>
              <td className="px-3 py-2 font-mono">{r.no_polisi}</td>
              <td className="px-3 py-2">{r.nama_supir || "-"}</td>
              <td className="px-3 py-2">{r.produk_nama || "-"}</td>
              <td className="px-3 py-2 font-mono text-right">{r.jumlah}</td>
              <td className="px-3 py-2 font-mono text-right">{r.jumlah_termuat}</td>
              <td className="px-3 py-2 text-xs uppercase">{statusLabel[r.status] || r.status}</td>
              <td className="px-3 py-2">{r.distrik_nama || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


export function ReportSummaryChip({ label, value }) {
  return (
    <div className="border border-border bg-background p-3">
      <div className="overline text-muted-foreground text-[10px]">{label}</div>
      <div className="text-2xl font-bold font-mono mt-1">{value}</div>
    </div>
  );
}
